import { Star } from "@phosphor-icons/react";

/** Read-only star strip (supports halves via rounding to nearest 0.5).
 *  Use <StarsInput /> for the interactive picker. */
export function Stars({ value = 0, max = 5, size = 14, showNumber = false, count = null, className = "" }) {
  const v = Math.max(0, Math.min(max, Number(value) || 0));
  const full = Math.floor(v);
  const hasHalf = v - full >= 0.25 && v - full < 0.75;
  const adjustedFull = v - full >= 0.75 ? full + 1 : full;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid="stars-readonly" aria-label={`Rated ${v} out of ${max}`}>
      <span className="inline-flex">
        {Array.from({ length: max }).map((_, i) => {
          const isFull = i < adjustedFull;
          const isHalf = !isFull && hasHalf && i === full;
          return (
            <Star
              key={i}
              size={size}
              weight={isFull ? "fill" : isHalf ? "duotone" : "regular"}
              className={isFull || isHalf ? "text-volt" : "text-muted-foreground/40"}
              data-testid={`star-readonly-${i}`}
            />
          );
        })}
      </span>
      {showNumber && (
        <span className="text-xs text-muted-foreground" data-testid="stars-number">
          {v ? v.toFixed(1) : "—"}{count !== null ? ` (${count})` : ""}
        </span>
      )}
    </span>
  );
}

/** Interactive 1–5 picker. Calls `onChange(n)` on click. */
export function StarsInput({ value = 0, onChange, size = 22, disabled = false, className = "" }) {
  return (
    <div className={`inline-flex items-center gap-1 ${className}`} role="radiogroup" aria-label="Your rating" data-testid="stars-input">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = n <= value;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange?.(n)}
            className={`p-1 rounded-sm transition-transform hover:scale-110 disabled:opacity-40 disabled:hover:scale-100 ${active ? "text-volt" : "text-muted-foreground/50 hover:text-volt"}`}
            data-testid={`star-input-${n}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
          >
            <Star size={size} weight={active ? "fill" : "regular"} />
          </button>
        );
      })}
    </div>
  );
}
